import { I18n } from '@boschebike/ebds-js';
import { ObjectKeyValueString } from '../helpers/types';

export const DEFAULT_CONTENT_LANG: string = 'en-GB';

export const AVAILABLE_CONTENT_LANG: ObjectKeyValueString = Object.freeze({
  de_AT: 'de-AT',
  de_CH: 'de-CH',
  de_DE: 'de-DE',
  de_LU: 'de-LU',
  en_AU: 'en-AU',
  en_CA: 'en-CA',
  en_GB: DEFAULT_CONTENT_LANG,
  en_NZ: 'en-NZ',
  en_US: 'en-US',
  es_ES: 'es-ES',
  fr_BE: 'fr-BE',
  fr_CA: 'fr-CA',
  fr_CH: 'fr-CH',
  fr_FR: 'fr-FR',
  fr_LU: 'fr-LU',
  it_IT: 'it-IT',
  it_CH: 'it-CH',
  ja_JP: 'ja-JP',
  ko_KR: 'ko-KR',
  nl_BE: 'nl-BE',
  nl_NL: 'nl-NL',
  pl_PL: 'pl-PL',
  zh_TW: 'zh-TW',
  cs_CZ: 'cs-CZ',
  da_DK: 'da-DK',
  fi_FI: 'fi-FI',
  no_NO: 'no-NO',
  pt_PT: 'pt-PT',
  sv_SE: 'sv-SE',
});

const contentDicts: any = Object.freeze({
  [AVAILABLE_CONTENT_LANG.de_AT]: () => import('../../static/de-AT.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.de_CH]: () => import('../../static/de-CH.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.de_DE]: () => import('../../static/de-DE.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.en_AU]: () => import('../../static/en-AU.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.en_CA]: () => import('../../static/en-CA.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.en_GB]: () => import('../../static/en-GB.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.en_NZ]: () => import('../../static/en-NZ.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.en_US]: () => import('../../static/en-US.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.es_ES]: () => import('../../static/es-ES.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.fr_BE]: () => import('../../static/fr-BE.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.fr_CA]: () => import('../../static/fr-CA.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.fr_CH]: () => import('../../static/fr-CH.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.fr_FR]: () => import('../../static/fr-FR.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.it_IT]: () => import('../../static/it-IT.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.ja_JP]: () => import('../../static/ja-JP.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.ko_KR]: () => import('../../static/ko-KR.json').then((res) => res.default),
  // nl-BE language is added as only Corporate Information page has some differences with nl-NL variant
  [AVAILABLE_CONTENT_LANG.nl_BE]: () => import('../../static/nl-BE.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.nl_NL]: () => import('../../static/nl-NL.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.pl_PL]: () => import('../../static/pl-PL.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.zh_TW]: () => import('../../static/zh-TW.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.cs_CZ]: () => import('../../static/cs-CZ.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.da_DK]: () => import('../../static/da-DK.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.fi_FI]: () => import('../../static/fi-FI.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.no_NO]: () => import('../../static/no-NO.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.pt_PT]: () => import('../../static/pt-PT.json').then((res) => res.default),
  [AVAILABLE_CONTENT_LANG.sv_SE]: () => import('../../static/sv-SE.json').then((res) => res.default),
});

// try the similar language as fallback
const defaultLanguageContent: ObjectKeyValueString = Object.freeze({
  de: AVAILABLE_CONTENT_LANG.de_DE,
  en: AVAILABLE_CONTENT_LANG.en_GB,
  es: AVAILABLE_CONTENT_LANG.es_ES,
  fr: AVAILABLE_CONTENT_LANG.fr_FR,
  it: AVAILABLE_CONTENT_LANG.it_IT,
  ja: AVAILABLE_CONTENT_LANG.ja_JP,
  ko: AVAILABLE_CONTENT_LANG.ko_KR,
  nl: AVAILABLE_CONTENT_LANG.nl_NL,
  zh: AVAILABLE_CONTENT_LANG.zh_TW,
});

export const getDefaultContentKey = (langCode = DEFAULT_CONTENT_LANG): string => (
  defaultLanguageContent[langCode.split('-')[0].toLowerCase()] || DEFAULT_CONTENT_LANG
);

export const getContentDict = async (langCode = DEFAULT_CONTENT_LANG): Promise<ObjectKeyValueString> => {
  if (!contentDicts[langCode]) {
    return contentDicts[getDefaultContentKey(langCode)]();
  }

  return contentDicts[langCode]();
};

export const getContent = async (langCode: string): Promise<I18n> => {
  const i18n = new I18n(DEFAULT_CONTENT_LANG, await getContentDict(langCode));

  return i18n;
};
